import { useEffect } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router';
import { Trans } from '@lingui/react/macro';

import { ClientCommunicationDrawer } from '@/components/comments/ClientCommunicationDrawer/ClientCommunicationDrawer';
import { GenericError } from '@/components/GenericError/GenericError';
import IllustrationError from '@/components/IllustrationError';
import Loader from '@/components/Loader';
import { DelayedChildren } from '@/components/Loader/Loader';
import {
  PageLayout,
  PageLayoutCenter,
  PageLayoutContent,
  PageLayoutHeader,
  PageLayoutLeftPart,
  PageLayoutLeftPartContent,
  PageLayoutRightPart,
  PageLayoutTopPart,
} from '@/components/PageLayout';
import { ScrollToAnchor } from '@/components/ScrollToAnchor/ScrollToAnchor';
import Box from '@/design_system/Box';
import FoldableSection from '@/design_system/FoldableSection';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { useProductAttributesByArticle } from '@/models/productAttribute';
import {
  isInDraftStep,
  isInFirstServiceChoiceStep,
  isInValidationStep,
  useRequest,
} from '@/models/request';
import { hasTransitStepsExceptClientPickup, useWorkflowPublic } from '@/models/workflow';
import { RequestContextProvider } from '@/routes/Requests/contexts/RequestContext';
import { RequestActivities } from '@/routes/Requests/Request/components/Activities/RequestActivities';
import { RequestArticles } from '@/routes/Requests/Request/components/RequestArticles';
import { RequestBreadcrumb } from '@/routes/Requests/Request/components/RequestBreadcrumb/RequestBreadcrumb';
import { RequestHeader } from '@/routes/Requests/Request/components/RequestHeader';
import {
  RequestInfoSidePanel,
  RequestInfoTopPanel,
} from '@/routes/Requests/Request/components/RequestInfo';
import ShipmentsTableForRequest from '@/routes/Requests/Request/components/ShipmentsTableForRequest';
import { ErrorBoundary } from '@/services/sentry';
import { createBEMClasses } from '@/utils/classname';
import { NotFoundError } from '@/utils/error/NotFoundError';
import { useResetSimpleBlocker } from '@/utils/navigation';
import { refreshBrowserTabTitle } from '@/utils/refreshBrowserTabTitle';
import { useScrollIntoView } from '@/utils/useScrollIntoView';
import useViewPort from '@/utils/useViewport';

import { RequestActionsFooter } from './components/RequestActionsFooter/RequestActionsFooter';
import { RequestComments } from './components/RequestComments';
import { RequestStepMessage } from './components/RequestStepMessage/RequestStepMessage';

import './Request.css';

const { block, element } = createBEMClasses('request');

export const Request = () => {
  const [commentsRef, scrollCommentsIntoView] = useScrollIntoView<HTMLDivElement>();
  const [containerRef, scrollContainerIntoView] = useScrollIntoView<HTMLDivElement>();

  const { isMobile } = useViewPort();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { data: request, isLoading: isRequestLoading, isError, error } = useRequest(id);
  const { data: workflow } = useWorkflowPublic(request?.workflowId);
  const productAttributesByArticle = useProductAttributesByArticle(request);

  const redirectToMonoArticle = searchParams.get('redirectToMonoArticle') === 'true';

  // Display the request reference in the browser tab title
  useEffect(() => {
    if (!request) {
      return;
    }

    const requestDetails = request.client?.name ?? request.store?.name;
    const requestReference = requestDetails
      ? `${request.reference} - ${requestDetails}`
      : request.reference;

    document.querySelector('meta[name="subtitle"]')?.setAttribute('content', requestReference);
    refreshBrowserTabTitle();

    return () => {
      document.querySelector('meta[name="subtitle"]')?.setAttribute('content', '');
      refreshBrowserTabTitle();
    };
  }, [request]);

  // Clear router blocker state after coming from requests/new
  useResetSimpleBlocker();

  if (isRequestLoading) {
    return (
      <PageLayoutCenter>
        <Loader style={{ height: '40px', width: '40px' }} />
        <Paragraph>
          <Trans id="_general.loading">Loading...</Trans>
        </Paragraph>
      </PageLayoutCenter>
    );
  }

  if (isError || !request) {
    return <RequestError error={error} />;
  }

  if (!request.referencedAt) {
    return (
      <PageLayoutCenter>
        <DelayedChildren>
          <Loader style={{ height: '40px', width: '40px' }} />
          <Paragraph>
            <Trans id="request.loading.unreferenced">Creating the request...</Trans>
          </Paragraph>
        </DelayedChildren>
      </PageLayoutCenter>
    );
  }

  if (redirectToMonoArticle && request.allArticles.length === 1) {
    return <Navigate to={`/requests/${request.id}/articles/${request.allArticles[0].id}`} />;
  }

  const showDeliveries =
    !!workflow &&
    hasTransitStepsExceptClientPickup(workflow) &&
    !(isInDraftStep(request) || isInFirstServiceChoiceStep(request) || isInValidationStep(request));

  return (
    <RequestContextProvider
      request={request}
      workflow={workflow}
      productAttributesData={productAttributesByArticle}
    >
      <PageLayout className={block()}>
        <PageLayoutHeader>
          <RequestHeader request={request} onCommentButtonPress={scrollCommentsIntoView} />
        </PageLayoutHeader>

        <PageLayoutContent>
          <PageLayoutLeftPart>
            <PageLayoutLeftPartContent>
              {isMobile && (
                <PageLayoutTopPart>
                  <RequestInfoTopPanel />
                </PageLayoutTopPart>
              )}

              <Stack className={element('main')} ref={containerRef}>
                <Stack gap="1rem">
                  {!isMobile && <RequestBreadcrumb request={request} />}
                  <ErrorBoundary>
                    <RequestStepMessage request={request} />
                  </ErrorBoundary>

                  <ErrorBoundary>
                    <RequestArticles />
                  </ErrorBoundary>
                </Stack>

                {showDeliveries && (
                  <FoldableSection
                    label={
                      <Title as="h2">
                        <Trans id="request.related-shipments">Deliveries</Trans>
                      </Title>
                    }
                  >
                    <ErrorBoundary>
                      <Box
                        padding="0"
                        removeStyleOnMobile
                        className={element('shipments', { 'no-background': isMobile })}
                        gap="1rem"
                      >
                        <ShipmentsTableForRequest request={request} />
                      </Box>
                    </ErrorBoundary>
                  </FoldableSection>
                )}

                <Stack ref={commentsRef}>
                  <FoldableSection
                    label={
                      <Title as="h2">
                        <Trans id="request.comment.title">Comments</Trans>
                      </Title>
                    }
                  >
                    <ErrorBoundary>
                      <Box
                        className={element('comment', { 'no-background': isMobile })}
                        padding="0"
                        removeStyleOnMobile
                      >
                        <RequestComments requestId={request.id} />
                      </Box>
                    </ErrorBoundary>
                  </FoldableSection>
                </Stack>

                <FoldableSection
                  label={
                    <Title as="h2" id="activity-section">
                      <Trans id="request.activity.title">Activity</Trans>
                    </Title>
                  }
                >
                  <ErrorBoundary>
                    <Box
                      className={element('activity', { 'no-background': isMobile })}
                      padding="0"
                      removeStyleOnMobile
                    >
                      <RequestActivities request={request} />
                    </Box>
                  </ErrorBoundary>
                </FoldableSection>
              </Stack>
            </PageLayoutLeftPartContent>

            <ErrorBoundary>
              <RequestActionsFooter request={request} onActionDone={scrollContainerIntoView} />
            </ErrorBoundary>
          </PageLayoutLeftPart>

          {!isMobile && (
            <PageLayoutRightPart>
              <RequestInfoSidePanel />
            </PageLayoutRightPart>
          )}
        </PageLayoutContent>

        <ScrollToAnchor />
      </PageLayout>
      <ClientCommunicationDrawer />
    </RequestContextProvider>
  );
};

const RequestError = ({ error }: { error: Error | null }) => {
  let errorMessage = <GenericError />;

  if (error instanceof NotFoundError) {
    errorMessage = (
      <Paragraph>
        <Trans id="request.error.not-found">Unknown request or you do not have access to it.</Trans>
      </Paragraph>
    );
  }

  return (
    <PageLayoutCenter>
      <IllustrationError />
      {errorMessage}
    </PageLayoutCenter>
  );
};
